import type { Background } from '@/api/types/background'
import {
  type FilenameMatchRank,
  matchBackgroundFilename,
} from '@/lib/backgrounds/match-background-filename'

/**
 * The Backgrounds tray's "Search by filename" filter. Matching is fuzzy (`matchBackgroundFilename`):
 * the exact text first, then the same text ignoring spaces, punctuation and the extension, then
 * words in any order, then close spellings — so near-miss terms still find the background.
 *
 * Results come back closest match first; within each closeness group `orderWithinRank` sets the
 * order (the tray passes its Sort option), and it orders the whole list when the query is blank.
 */
export function filterBackgroundsByFilename(
  backgrounds: Background[],
  query: string,
  orderWithinRank: (group: Background[]) => Background[] = (group) => group
): Background[] {
  if (!query.trim()) return orderWithinRank(backgrounds)

  const groups = new Map<FilenameMatchRank, Background[]>()

  for (const background of backgrounds) {
    const rank = matchBackgroundFilename(background.displayName, query)

    if (rank === null) continue

    const group = groups.get(rank)

    if (group) {
      group.push(background)
    } else {
      groups.set(rank, [background])
    }
  }

  return [...groups.keys()]
    .sort((a, b) => a - b)
    .flatMap((rank) => orderWithinRank(groups.get(rank) ?? []))
}
